import React, { useState, useEffect } from 'react';
import { FiX, FiServer, FiCloud } from 'react-icons/fi';
import TagPairEditor from './TagPairEditor';
import type { WorkloadSummary, WorkloadTagPair, WorkloadCreate, WorkloadUpdate } from '../../../../types/workload';
import type { AWSAccountSummary, AWSEnvironmentSummary } from '../../../../contexts/FilterContext';

interface WorkloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: WorkloadCreate | WorkloadUpdate) => Promise<void>;
  workload?: WorkloadSummary | null;
  accounts: AWSAccountSummary[];
  environments: AWSEnvironmentSummary[];
  projectId?: string; // If set, auto-link this project on creation
}

const WorkloadModal: React.FC<WorkloadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workload,
  accounts,
  environments,
  projectId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagPairs, setTagPairs] = useState<WorkloadTagPair[]>([
    { tag_key: '', tag_value: '', operator: null },
  ]);
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<string[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [costTrackingEnabled, setCostTrackingEnabled] = useState(false);
  const [showCostTrackingHelp, setShowCostTrackingHelp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!workload;

  useEffect(() => {
    if (workload) {
      setName(workload.name);
      setDescription(workload.description || '');
      setTagPairs(workload.tag_pairs.length > 0 ? workload.tag_pairs : [{ tag_key: '', tag_value: '', operator: null }]);
      setSelectedEnvironmentIds(workload.environment_ids || []);
      setSelectedAccountIds(workload.account_ids || []);
      setCostTrackingEnabled(workload.cost_tracking_enabled ?? false);
    } else {
      setName('');
      setDescription('');
      setTagPairs([{ tag_key: '', tag_value: '', operator: null }]);
      setSelectedEnvironmentIds([]);
      setSelectedAccountIds([]);
      setCostTrackingEnabled(false);
    }
    setError(null);
  }, [workload, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Workload name is required');
      return;
    }

    const validPairs = tagPairs.filter(p => p.tag_key.trim() && p.tag_value.trim());
    if (validPairs.length === 0) {
      setError('At least one tag pair with key and value is required');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await onSave({
          name: name.trim(),
          description: description.trim() || undefined,
          tag_pairs: validPairs,
          environment_ids: selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : undefined,
          account_ids: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
          cost_tracking_enabled: costTrackingEnabled,
        } as WorkloadUpdate);
      } else {
        await onSave({
          name: name.trim(),
          description: description.trim() || undefined,
          tag_pairs: validPairs,
          environment_ids: selectedEnvironmentIds.length > 0 ? selectedEnvironmentIds : undefined,
          account_ids: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
          project_id: projectId,
          cost_tracking_enabled: costTrackingEnabled,
        } as WorkloadCreate);
      }
      onClose();
    } catch {
      setError('Failed to save workload');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEnvironment = (envId: string) => {
    setSelectedEnvironmentIds(prev =>
      prev.includes(envId) ? prev.filter(id => id !== envId) : [...prev, envId]
    );
  };

  const toggleAccount = (accId: string) => {
    setSelectedAccountIds(prev =>
      prev.includes(accId) ? prev.filter(id => id !== accId) : [...prev, accId]
    );
  };

  // Group environments by account
  const envsByAccount = new Map<string, AWSEnvironmentSummary[]>();
  for (const env of environments) {
    const accId = env.aws_account_id;
    if (!envsByAccount.has(accId)) envsByAccount.set(accId, []);
    envsByAccount.get(accId)!.push(env);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Workload' : 'Create Workload'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-md">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Workload Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Application/Workload Name"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this workload represents..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Tag Pairs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tag Pairs *
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Define tag key/value pairs to identify AWS resources for this workload. Use AND/OR to combine conditions.
            </p>
            <TagPairEditor tagPairs={tagPairs} onChange={setTagPairs} />
          </div>

          {/* Cost Tracking */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={costTrackingEnabled}
                onChange={(e) => setCostTrackingEnabled(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 h-4 w-4"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Cost Tracking
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              Track infrastructure costs for resources matching this workload&apos;s tags.{' '}
              <button
                type="button"
                onClick={() => setShowCostTrackingHelp(prev => !prev)}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                Learn more
              </button>
            </p>
            {showCostTrackingHelp && (
              <div className="mt-2 ml-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-xs text-gray-700 dark:text-gray-300 space-y-2">
                <p className="font-medium text-blue-800 dark:text-blue-200">How cost tracking works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Aivilo Lens matches your workload tag pairs against AWS resource tags to allocate costs.</li>
                  <li><strong>AWS Cost Allocation Tags:</strong> For costs to appear in Cost Explorer breakdowns, the tag key used here (e.g., &quot;Workload&quot;) must be activated as a <strong>Cost Allocation Tag</strong> in your AWS Billing console.</li>
                  <li><strong>AND/OR logic:</strong> When using AND, a resource must have ALL tags in the group. When using OR, matching ANY group is sufficient. Cost allocation uses the <strong>primary tag key</strong> (the most common one across your workloads).</li>
                  <li><strong>Multiple tags:</strong> If your workload uses multiple tag keys with AND logic, the cost is attributed when the resource matches all conditions. The primary tag key is used for Cost Explorer grouping.</li>
                </ul>
              </div>
            )}
          </div>

          {/* Environment & Account Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Scope
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Optionally scope this workload to specific environments or accounts. Leave empty to apply across all.
            </p>

            {environments.length === 0 && accounts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No environments or accounts configured. Configure them in Settings &gt; AWS Environments.
              </p>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-3">
                {/* Environments grouped by account */}
                {Array.from(envsByAccount.entries()).map(([accId, envs]) => {
                  const account = accounts.find(a => a.id === accId);
                  const accountLabel = account ? account.account_id : accId;
                  return (
                    <div key={accId}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <FiCloud className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          {accountLabel}
                        </span>
                      </div>
                      <div className="ml-5 space-y-1">
                        {envs.map(env => (
                          <label
                            key={env.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedEnvironmentIds.includes(env.id)}
                              onChange={() => toggleEnvironment(env.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <FiServer className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-white">{env.name}</span>
                            {env.environment_type && (
                              <span className="text-xs text-gray-400">({env.environment_type})</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Standalone accounts (without environments) */}
                {accounts.filter(a => !envsByAccount.has(a.id)).length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <FiCloud className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        Accounts (no environments)
                      </span>
                    </div>
                    <div className="ml-5 space-y-1">
                      {accounts.filter(a => !envsByAccount.has(a.id)).map(account => (
                        <label
                          key={account.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAccountIds.includes(account.id)}
                            onChange={() => toggleAccount(account.id)}
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-mono text-gray-900 dark:text-white">{account.account_id}</span>
                          <span className="text-xs text-gray-400">({account.account_type})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Workload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkloadModal;
